import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeListQuery } from '../../common/list-query/list-query';
import { BookingOutcomeSchema, BookingStatusSchema } from '../booking/booking';

/**
 * The bookings list — one schema serving the member's own bookings and the
 * staff oversight table.
 *
 * **Who may see which rows is not a filter.** A member's request is scoped to
 * their own bookings by the controller, from the JWT; staff see the estate
 * because they hold `meetings.read`. Neither scoping is expressible here, and
 * that is the point — a filter a client can send is a filter a client can omit.
 *
 * `from`/`to` bound the **slot's** window rather than the booking's creation,
 * because every question anybody asks of this list is about when people are
 * meeting: "who is at this venue on Thursday", "what is coming up this week".
 * The interval is half-open, matching the slot rules P25b1 established so
 * "does this window show?" and "does this window clash?" cannot disagree.
 *
 * This schema decides which parameters may be **sent**; `BOOKING_FILTER_SPEC` on
 * the api decides which column and operator each one reaches (N7). Both halves
 * are needed — a whitelist here with a passthrough there would be no whitelist.
 *
 * `outcome` arrived in P25d, with the spec entry and the control that send it.
 * It narrows on the column alone and does **not** imply `status = 'completed'`:
 * the table's own CHECK already guarantees an outcome only ever sits on a
 * completed booking, and a filter that quietly adds a second condition is not a
 * whitelist. There is deliberately no "has no outcome" companion, because
 * "completed with nothing recorded" is a state the CHECK makes unrepresentable.
 *
 * `endedBefore` bounds the slot's **end**, where `to` bounds its start: it is
 * the staff queue's question — "which scheduled meetings are over and still
 * have no outcome recorded" is `status=scheduled&endedBefore=<now>` — and a
 * meeting in progress must not appear in it, which `to` could not promise.
 */
export const BookingsListQuerySchema = makeListQuery({
  status: BookingStatusSchema.optional(),
  outcome: BookingOutcomeSchema.optional(),
  venueId: UuidSchema.optional(),
  matchId: UuidSchema.optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  endedBefore: z.iso.datetime().optional(),
});

export type BookingsListQuery = z.infer<typeof BookingsListQuerySchema>;
